import { ReactNode, useState } from 'react';
import { Control, useFieldArray, useWatch } from 'react-hook-form';
import CopyIcon from '@/assets/icons/copy-03.svg?react';
import TrashIcon from '@/assets/icons/trash-02.svg?react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import PlusIcon from '@/assets/icons/plus.svg?react';
import { HotelFormDataWithTranslations } from '@/i18n/translation-type-utils';
import { useTranslation } from '@/i18n';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Label } from '../ui/label';
import StyledInput from '../styled/StyledInput';
import StyledDatePicker from '../styled/StyledDatePicker';
import StyledCheckboxGroup from '../styled/StyledCheckboxGroup';
import StyledButton from '../styled/StyledButton';
import StyledTooltipContainer from '../styled/StyledTooltipContainer';
import { WEEKDAYS, WEEKDAYS_TRANSLATED } from '@/constants/weekdays';
import { useTranslatedConst } from '@/hooks/useTranslatedConst';
import {
  getRoomPensionOptions,
  toHotelPensionType,
} from '@/constants/hotelPension';
import { getPensionTypesAbove } from '@/utils/estimates/hotel/computeHotelPensionPrice';
import {
  buildEmptyHotelSupplement,
  buildEmptyHotelSupplementPeriod,
  buildEmptyHotelSupplementPricing,
} from '@/utils/benefits/hotel/buildEmptyHotelSupplementCard';
import type { HotelPensionType } from '@/types/benefits/hotel/HotelResponse';
import clsx from 'clsx';
import { addDays } from 'date-fns';
import SupplementTariffPaste from './excelPaste/SupplementTariffPaste';
import SupplementTariffsPaste from './excelPaste/SupplementTariffsPaste';
import WithExcelPastePanel from './excelPaste/WithExcelPastePanel';
import {
  EXPERT_CHILDREN_WIDTH,
  PASTE_HOST_PROPS,
} from './excelPaste/expertLayout';
import { useExpertMode } from './excelPaste/useExpertMode';
import { toChildBandsCount } from './excelPaste/pasteFormHelpers';

interface HotelSupplementsProps {
  control: Control<HotelFormDataWithTranslations>;
  isViewMode?: boolean;
}

const HotelSupplements = ({
  control,
  isViewMode = false,
}: HotelSupplementsProps) => {
  const { t } = useTranslation();

  const { fields, append, remove, replace } = useFieldArray({
    control,
    name: 'hotelSupplements',
  });

  const supplements = useWatch({ control, name: 'hotelSupplements' });

  const pricingPensionType = useWatch({ control, name: 'pricingPensionType' });
  const hotelPensionType = toHotelPensionType(pricingPensionType?.value);
  const acceptsChildPricing = useWatch({
    control,
    name: 'acceptsChildPricing',
  });
  const childAges = useWatch({ control, name: 'childAges' });

  const numberOfChildDistributions =
    acceptsChildPricing === 'true' ? (childAges?.length ?? 0) : 0;

  const relevantPensionTypes = getPensionTypesAbove(pricingPensionType?.value);
  const displayedPensionTypes = hotelPensionType
    ? [hotelPensionType, ...relevantPensionTypes]
    : relevantPensionTypes;

  const handleAddCard = () => append(buildEmptyHotelSupplement());

  const handleDuplicate = (index: number) => {
    const current = supplements[index];
    const copy = {
      ...current,
      id: undefined,
      name: `${current.name || t('hotels.supplement_', { index: index + 1 })} (${t('common.copy')})`,
      pensionTypes: current.pensionTypes ?? [],
      pricingList: (current.pricingList ?? []).map((pricing) => ({
        ...pricing,
        id: undefined,
        periods: (pricing.periods ?? []).map((period) => ({
          ...period,
          id: undefined,
          applicationDays: period.applicationDays ?? [],
        })),
        pricing: pricing.pricing
          ? {
              ...pricing.pricing,
              id: undefined,
              adultPrice: pricing.pricing.adultPrice ?? null,
              childPrice: pricing.pricing.childPrice ?? null,
              adolescentPrice: pricing.pricing.adolescentPrice ?? null,
            }
          : undefined,
      })),
    };
    append(copy);
  };

  return (
    <div {...PASTE_HOST_PROPS} className="flex flex-col gap-4">
      {fields.map((field, cardIndex) => (
        <HotelSupplementCard
          key={field.id}
          control={control}
          cardIndex={cardIndex}
          isViewMode={isViewMode}
          displayedPensionTypes={displayedPensionTypes}
          numberOfChildDistributions={numberOfChildDistributions}
          childAges={childAges}
          onDelete={() => remove(cardIndex)}
          onDuplicate={() => handleDuplicate(cardIndex)}
          // Every festive tariff at once, above the first tariff's panel.
          allTariffsPanel={
            cardIndex === 0 ? (
              <SupplementTariffsPaste
                childBandsCount={toChildBandsCount(numberOfChildDistributions)}
                childAges={childAges}
                offeredPensionTypes={displayedPensionTypes}
                replaceSupplements={replace}
              />
            ) : null
          }
        />
      ))}
      {!isViewMode && (
        <StyledButton
          title={t('hotels.addSupplement')}
          onClick={handleAddCard}
          type="button"
          variant="outlined"
          className="text-brand-600 w-fit"
          icon={<PlusIcon className="size-5 fill-brand-600" />}
        />
      )}
    </div>
  );
};

interface HotelSupplementCardProps {
  control: Control<HotelFormDataWithTranslations>;
  cardIndex: number;
  isViewMode: boolean;
  displayedPensionTypes: HotelPensionType[];
  numberOfChildDistributions: number;
  childAges: Array<{ childAgeFrom?: number; childAgeTo?: number }> | undefined;
  onDelete: () => void;
  onDuplicate: () => void;
  /** Paste of every festive tariff, shown above the first tariff's panel. */
  allTariffsPanel?: ReactNode;
}

function HotelSupplementCard({
  control,
  cardIndex,
  isViewMode,
  displayedPensionTypes,
  numberOfChildDistributions,
  childAges,
  onDelete,
  onDuplicate,
  allTariffsPanel,
}: HotelSupplementCardProps) {
  const { t } = useTranslation();
  const [isFolded, setIsFolded] = useState(false);
  const isExpertMode = useExpertMode() && !isViewMode;

  const cardPrefix = `hotelSupplements.${cardIndex}` as const;
  const pricingListName = `hotelSupplements.${cardIndex}.pricingList` as const;

  const {
    fields: pricingFields,
    append: appendPricing,
    remove: removePricing,
  } = useFieldArray({
    control,
    name: pricingListName,
  });

  const firstTarifApplicationDays = useWatch({
    control,
    name: `${pricingListName}.0.periods.0.applicationDays`,
  });

  const currentTitle = useWatch({
    control,
    name: `${cardPrefix}.name`,
  });

  return (
    <Card className="w-full">
      <CardHeader className="flex flex-row justify-between">
        <CardTitle
          className="text-base leading-6 hover:cursor-pointer"
          onClick={() => setIsFolded(!isFolded)}
        >
          {currentTitle || t('hotels.supplement_', { index: cardIndex + 1 })}
        </CardTitle>
        <div className="flex gap-10">
          <div className="flex gap-4">
            {!isViewMode && (
              <StyledTooltipContainer tooltipMessage={t('common.duplicate')}>
                <CopyIcon
                  className="size-5 stroke-brand-600 hover:cursor-pointer"
                  onClick={() => {
                    setIsFolded(true);
                    onDuplicate();
                  }}
                />
              </StyledTooltipContainer>
            )}
            {cardIndex > 0 && !isViewMode && (
              <StyledTooltipContainer tooltipMessage={t('common.delete')}>
                <TrashIcon
                  className="size-5 stroke-brand-600 hover:cursor-pointer"
                  onClick={onDelete}
                />
              </StyledTooltipContainer>
            )}
          </div>
          {isFolded ? (
            <ChevronDown
              className="size-5 hover:cursor-pointer"
              onClick={() => setIsFolded(false)}
            />
          ) : (
            <ChevronUp
              className="size-5 hover:cursor-pointer"
              onClick={() => setIsFolded(true)}
            />
          )}
        </div>
      </CardHeader>
      {!isFolded && (
        <CardContent className="flex flex-col gap-6">
          <div
            className={clsx('flex flex-col gap-4', {
              [EXPERT_CHILDREN_WIDTH]: isExpertMode,
            })}
          >
            <StyledInput
              control={control}
              name={`${cardPrefix}.name`}
              placeholder={t('hotels.supplementNamePlaceholder')}
              disabled={isViewMode}
            />
            <div className="flex flex-col gap-1.5">
              <Label className="text-gray-700">
                {t('hotels.supplementConcernsTypes')}
              </Label>
              <StyledCheckboxGroup
                control={control}
                name={`${cardPrefix}.pensionTypes`}
                options={getRoomPensionOptions(t).filter((o) =>
                  displayedPensionTypes.includes(o.value),
                )}
                isViewMode={isViewMode}
                displayAllOption={false}
                displayInColumns
              />
            </div>
            {pricingFields.map((tarifField, tarifIndex) => (
              <WithExcelPastePanel
                key={tarifField.id}
                isViewMode={isViewMode}
                panel={
                  <div className="flex flex-col gap-4">
                    {tarifIndex === 0 && allTariffsPanel}
                    <SupplementTariffPaste
                      supplementIndex={cardIndex}
                      tariffIndex={tarifIndex}
                      childBandsCount={toChildBandsCount(
                        numberOfChildDistributions,
                      )}
                      childAges={childAges}
                      offeredPensionTypes={displayedPensionTypes}
                    />
                  </div>
                }
              >
                <div className="flex flex-col gap-4 rounded-md border border-gray-200 p-4">
                  <div className="flex justify-between items-center">
                    <Label className="text-gray-700 font-medium">
                      {t('tariffs.tarif_', { index: tarifIndex + 1 })}
                    </Label>
                    <div className="flex items-center gap-4">
                      {pricingFields.length > 1 && !isViewMode && (
                        <StyledButton
                          title={t('common.delete')}
                          className="p-0 h-auto"
                          variant="link"
                          onClick={() => removePricing(tarifIndex)}
                        />
                      )}
                    </div>
                  </div>
                  <StyledInput
                    control={control}
                    name={`${pricingListName}.${tarifIndex}.name`}
                    placeholder={t('tariffs.placeholder')}
                    disabled={isViewMode}
                  />
                  <HotelSupplementTarifPeriods
                    control={control}
                    periodsName={`${pricingListName}.${tarifIndex}.periods`}
                    tarifIndex={tarifIndex}
                    isViewMode={isViewMode}
                  />
                  <table className="w-fit">
                    <thead>
                      <tr>
                        <th className="text-left p-2" />
                        <th className="text-left p-2">
                          <Label className="text-gray-700">
                            {t('hotels.adult')}
                          </Label>
                        </th>
                        {numberOfChildDistributions > 1 && childAges && (
                          <th className="text-left p-2">
                            <Label className="text-gray-700">
                              {t('hotels.childAge', {
                                ageFrom: childAges[1].childAgeFrom ?? 12,
                                ageTo: childAges[1].childAgeTo ?? 17,
                              })}
                            </Label>
                          </th>
                        )}
                        {numberOfChildDistributions > 0 && childAges && (
                          <th className="text-left p-2">
                            <Label className="text-gray-700">
                              {t('hotels.childAge', {
                                ageFrom: childAges[0].childAgeFrom ?? 2,
                                ageTo: childAges[0].childAgeTo ?? 11,
                              })}
                            </Label>
                          </th>
                        )}
                        <th className="text-left p-2" />
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td className="p-2">
                          <Label className="text-gray-700">
                            {t('hotels.supplementTarif')}
                          </Label>
                        </td>
                        <td className="p-2 w-24">
                          <StyledInput
                            control={control}
                            name={`${pricingListName}.${tarifIndex}.pricing.adultPrice`}
                            type="number"
                            min={0}
                            disabled={isViewMode}
                            hideNumberFieldArrows
                          />
                        </td>
                        {numberOfChildDistributions > 1 && childAges && (
                          <td className="p-2 w-24">
                            <StyledInput
                              control={control}
                              name={`${pricingListName}.${tarifIndex}.pricing.adolescentPrice`}
                              type="number"
                              min={0}
                              disabled={isViewMode}
                              hideNumberFieldArrows
                            />
                          </td>
                        )}
                        {numberOfChildDistributions > 0 && childAges && (
                          <td className="p-2 w-24">
                            <StyledInput
                              control={control}
                              name={`${pricingListName}.${tarifIndex}.pricing.childPrice`}
                              type="number"
                              min={0}
                              disabled={isViewMode}
                              hideNumberFieldArrows
                            />
                          </td>
                        )}
                        <td className="p-2">
                          <Label className="text-gray-700">
                            {t('hotels.by_person')}
                          </Label>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </WithExcelPastePanel>
            ))}
            {!isViewMode && (
              <StyledButton
                className="text-brand-600 w-fit pl-0"
                title={t('tariffs.addTarif')}
                onClick={() => {
                  appendPricing({
                    ...buildEmptyHotelSupplementPricing(),
                    periods: [
                      {
                        startDate: null,
                        endDate: null,
                        applicationDays: firstTarifApplicationDays ?? [],
                      },
                    ],
                  });
                }}
                variant="link"
                icon={<PlusIcon className="size-5 fill-brand-600" />}
              />
            )}
          </div>
        </CardContent>
      )}
    </Card>
  );
}

interface HotelSupplementTarifPeriodsProps {
  control: Control<HotelFormDataWithTranslations>;
  periodsName: `hotelSupplements.${number}.pricingList.${number}.periods`;
  tarifIndex: number;
  isViewMode: boolean;
}

function HotelSupplementTarifPeriods({
  control,
  periodsName,
  tarifIndex,
  isViewMode,
}: HotelSupplementTarifPeriodsProps) {
  const { t } = useTranslation();
  const getTranslatedConst = useTranslatedConst();

  const { fields, append, remove } = useFieldArray({
    control,
    name: periodsName,
  });

  const firstPeriodApplicationDays = useWatch({
    control,
    name: `${periodsName}.0.applicationDays`,
  });

  const periodValues = useWatch({ control, name: periodsName });

  // Boundaries mirror the room-category periods: an end date cannot precede
  // its start date, and a period must start after the previous period ends.
  const getStartDateValue = (index: number, add = 0) =>
    periodValues?.[index]?.startDate
      ? addDays(new Date(periodValues[index].startDate), add)
      : undefined;

  const getEndDateValue = (index: number, add = 0) =>
    periodValues?.[index]?.endDate
      ? addDays(new Date(periodValues[index].endDate), add)
      : undefined;

  return (
    <div className="flex flex-col gap-4">
      {fields.map((periodField, periodIndex) => (
        <div
          key={periodField.id}
          className={clsx('flex flex-col', {
            'w-full gap-1.5': !isViewMode,
            'lg:flex-row md:justify-between gap-6': isViewMode,
          })}
        >
          <div className={clsx({ 'flex flex-col w-full': isViewMode })}>
            <div className="flex justify-between items-center gap-2">
              <Label className="text-gray-700">
                {t('tariffs.period_', {
                  index: periodIndex + 1,
                  tarifIndex: tarifIndex + 1,
                })}
                {' *'}
              </Label>
              {fields.length > 1 && !isViewMode && (
                <StyledButton
                  title={t('common.delete')}
                  className="p-0 h-auto"
                  variant="link"
                  onClick={() => remove(periodIndex)}
                />
              )}
            </div>
            <div
              className={clsx('flex items-center gap-2', {
                'flex-col md:flex-row w-full': !isViewMode,
              })}
            >
              <span>{t('common.from')}</span>
              <StyledDatePicker
                control={control}
                name={`${periodsName}.${periodIndex}.startDate`}
                className="w-full"
                isViewMode={isViewMode}
                earliestDate={getEndDateValue(periodIndex - 1, 1)}
                enforceCurrentDateBoundary={false}
              />
              <span>{t('common.to')}</span>
              <StyledDatePicker
                control={control}
                name={`${periodsName}.${periodIndex}.endDate`}
                className="w-full"
                isViewMode={isViewMode}
                earliestDate={getStartDateValue(periodIndex, 0)}
                enforceCurrentDateBoundary={false}
              />
            </div>
          </div>
          <div className="flex flex-col w-full gap-1.5">
            <Label className="text-gray-700">{t('tariffs.validDays')} *</Label>
            <StyledCheckboxGroup
              control={control}
              name={`${periodsName}.${periodIndex}.applicationDays`}
              options={WEEKDAYS.map((weekday) => ({
                value: weekday,
                label: getTranslatedConst(WEEKDAYS_TRANSLATED)[weekday][0],
              }))}
              isViewMode={isViewMode}
            />
          </div>
        </div>
      ))}
      {!isViewMode && (
        <StyledButton
          className="text-brand-600 w-fit pl-0"
          title={t('tariffs.addPeriod')}
          onClick={() =>
            append({
              ...buildEmptyHotelSupplementPeriod(),
              applicationDays: firstPeriodApplicationDays ?? WEEKDAYS,
            })
          }
          variant="link"
          icon={<PlusIcon className="size-5 fill-brand-600" />}
        />
      )}
    </div>
  );
}

export default HotelSupplements;
